import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { ApplicableOrNotApplicable } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkStepper from "../../components/atoms/TkStepper";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkHelpIcon from "../../components/atoms/TkHelpIcon";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { API_STA008_BuildReqParams } from "../../utils/buildRequestParams";
import { IAPI_STA008_Request } from "../../type/apiType";
import { httpAPI_STA008 } from "./api/request";
import { fakeTableList, schema1, schema2, steps } from "./parts";
import "./_style.scss";
import _ from "lodash";

/**
 * SCRC060127 退職情報　登録（退職者の基本情報登録）
 * 職員の退職時に継続異動かの判定する情報を登録する。
 * @return SCRC060127
 */
const SCRC060127 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);

  const userTypeKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const context = businessState.job_32;
  const applicable = ApplicableOrNotApplicable["該当"];

  const [stateTableList, setStateTableList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [open, setOpen] = useState(false);
  const [agreementFlgValue, setAgreementFlgValue] = useState("");
  const [employmentDayFlgValue, setEmploymentDayFlgValue] = useState("");
  const [isJob_33, setIsJob_33] = useState(false);

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;

    // 契約者（法人）名
    let contractorName = resArr.find(
      (item) => item.keyword === "contractorName"
    )!;
    contractorName.contractorNumber =
      businessState.common?.contractorNo +
      ":" +
      businessState.common?.contractorName;
    contractorName.showLine = userTypeKiko;

    // 職員番号
    resArr.find((item) => item.keyword === "staffNo")!.text = context?.staffNo;

    // 退職者名
    resArr.find((item) => item.keyword === "retirementStaffName")!.text =
      context?.staffNameKanji;

    // 現在の共済契約者と転職先の共済契約者が、施設運営上の理由により人材を融通することを事前に合意した転職である
    resArr.find(
      (item) => item.keyword === "mutualTransferAgreementFlg"
    )!.setDefaultValue = context?.continueTransferApplicable1Flg;

    // 現在の共済契約者の退職日の翌日が、転職先の共済契約者への就職日である
    resArr.find(
      (item) => item.keyword === "mutualTransferDateFlg"
    )!.setDefaultValue = context?.continueTransferApplicable2Flg;

    // 転職先の共済契約者番号
    resArr.find((item) => item.keyword === "aContractorNo")!.setDefaultValue =
      context?.aContractorNo;

    // 転職先の共済契約者名
    resArr.find((item) => item.keyword === "aContractorName")!.setDefaultValue =
      context?.aCorpName;

    setAgreementFlgValue(context?.continueTransferApplicable1Flg ?? "");
    setEmploymentDayFlgValue(context?.continueTransferApplicable2Flg ?? "");
    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let mutualTransferAgreementFlgType = resArr.find(
      (item) => item.keyword === "mutualTransferAgreementFlg"
    )!;
    let mutualTransferDateFlgType = resArr.find(
      (item) => item.keyword === "mutualTransferDateFlg"
    )!;

    // 現在の共済契約者と転職先の共済契約者が、施設運営上の理由により人材を融通することを事前に合意した転職である
    mutualTransferAgreementFlgType.onGetValue = (data: any) => {
      setAgreementFlgValue(data);
    };

    // 現在の共済契約者の退職日の翌日が、転職先の共済契約者への就職日である
    mutualTransferDateFlgType.onGetValue = (data: any) => {
      setEmploymentDayFlgValue(data);
    };
    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ラジオボタン　選択
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let aContractorNo = resArr.find(
      (item) => item.keyword === "aContractorNo"
    )!;
    let aContractorName = resArr.find(
      (item) => item.keyword === "aContractorName"
    )!;

    let flg =
      agreementFlgValue === applicable && employmentDayFlgValue === applicable;

    if (aContractorNo.showLine === flg) return;

    aContractorNo.showLine = flg;
    aContractorName.showLine = flg;

    setIsJob_33(flg);
    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementFlgValue, employmentDayFlgValue]);

  const unrecognizedMonthCheck = (month: any): "0" | "1" => {
    let tmpContext: any = context;
    if (tmpContext?.[month] === "1" || tmpContext?.[month] === "3") {
      return "1";
    } else {
      return "0";
    }
  };

  const makeHttpObj = (obj: any): IAPI_STA008_Request => {
    const reqObj: IAPI_STA008_Request = {
      ...context,
      ...obj,
      contractorNo: userTypeKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo, // 共済契約者番号
      tempSaveStep: 1, // 一時保存ステップ
      businessType: isJob_33 ? "JOB-33" : "JOB-32", // 業務タイプ
      retireReasonCd: context?.nRetireReasonCd, // 退職理由コード
      changedName: context?.staffNameKanji, // 変更後氏名（漢字）
      changedNameKana: context?.staffNameKana, // 変更後氏名ｶﾅ
      apr: unrecognizedMonthCheck("apr"), // 4月
      may: unrecognizedMonthCheck("may"), // 5月
      jun: unrecognizedMonthCheck("jun"), //	6月
      jul: unrecognizedMonthCheck("jul"), // 7月
      aug: unrecognizedMonthCheck("aug"), //	8月
      sep: unrecognizedMonthCheck("sep"), // 9月
      oct: unrecognizedMonthCheck("oct"), // 10月
      nov: unrecognizedMonthCheck("nov"), //	11月
      dec: unrecognizedMonthCheck("dec"), //	12月
      jan: unrecognizedMonthCheck("jan"), // 1月
      feb: unrecognizedMonthCheck("feb"), // 2月
      mar: unrecognizedMonthCheck("mar"), // 3月
      formProspectNum: context?.withholdingTaxSlipTotal, // 源泉徴収票登録枚数
      screenId: "SCRC060127", // 画面id
    };

    return API_STA008_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    const dataObj = {
      aContractorNo: data.aContractorNo,
      aCorpName: data.aContractorName,
      continueTransferApplicable1Flg: data.mutualTransferAgreementFlg,
      continueTransferApplicable2Flg: data.mutualTransferDateFlg,
    };

    switch (index) {
      case 0: // 一時保存
        try {
          // 退職者詳細情報更新API実行
          const res = await httpAPI_STA008({
            ...makeHttpObj(dataObj),
          });

          if (res.resultCd === "I0000") {
            setState({ ...state, errorMsgId: "" });
            setBusinessState({
              ...businessState,
              job_32: {
                ...context,
                tempSaveStep: 1,
                applyId: res.applyId,
                updateDateTime: res.updateDateTime,
                ...dataObj,
              },
            });
            setOpen(true);
          } else {
            setState({ ...state, errorMsgId: res.resultCd });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        // 持回り情報
        setBusinessState({
          ...businessState,
          common: {
            ...businessState.common,
            businessType: isJob_33 ? "JOB-33" : "JOB-32",
          },
          job_32: {
            ...context,
            ...dataObj,
          },
        });

        if (isJob_33) {
          navigate("/SCRC060122");
        } else {
          navigate("/SCRC060121");
        }
        break;
    }
  };

  return (
    <Box data-testid="SCRC060127test">
      <TkStepper
        steps={steps}
        activeStep={0}
        savaStep={context?.tempSaveStep}
      />

      <Box className="SCRC060127InfoManual">
        <TkInfoManual>{labelids.labelid.LBLC060127_01}</TkInfoManual>
        <TkHelpIcon
          helpMessage={
            "次の条件をいずれも満たした場合の転職は今までの勤務期間（被共済職員期間）を次の転職先での勤務期間（被共済職員期間）と通算することができます。"
          }
        />
      </Box>

      <TkFakeTable
        list={stateTableList}
        ruleSchema={isJob_33 ? { ...schema1, ...schema2 } : schema1}
        buttonGroup={<TkBackButton useBackParam />}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        onCheckAndSubmit={(data, index) => handleSubmit(data, index)}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRC060127;
